/**
 * Territory System - handles territory control based on military presence
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import type { BuildingComponent } from '@/game/components/Building';
import type { FactionId } from '@/types';
import { EventBus } from '@/core/events/EventBus';

interface TileControl {
  owner: FactionId | null;
  presence: Map<FactionId, number>;
}

export class TerritorySystem implements System {
  private territoryGrid: Map<string, TileControl> = new Map();
  private tileSize: number;
  private updateInterval: number = 0.5; // Update every 0.5 seconds for more responsive territory
  private timeSinceUpdate: number = 0;
  private pendingForceUpdate: boolean = false;
  private defeatedFactions: Set<FactionId> = new Set();

  // Callback for when territory colors need updating
  public onTerritoryChange?: (tileX: number, tileZ: number, newOwner: FactionId | null) => void;

  constructor(tileSize: number = 1.0) {
    this.tileSize = tileSize;
  }

  private getTileKey(x: number, z: number): string {
    const tx = Math.floor(x / this.tileSize);
    const tz = Math.floor(z / this.tileSize);
    return `${tx},${tz}`;
  }

  private parseTileKey(key: string): { x: number; z: number } {
    const [x, z] = key.split(',').map(Number);
    return { x, z };
  }

  getTileOwner(worldX: number, worldZ: number): FactionId | null {
    const key = this.getTileKey(worldX, worldZ);
    return this.territoryGrid.get(key)?.owner ?? null;
  }

  setTileOwner(worldX: number, worldZ: number, owner: FactionId): void {
    const key = this.getTileKey(worldX, worldZ);
    let tile = this.territoryGrid.get(key);

    if (!tile) {
      tile = { owner: null, presence: new Map() };
      this.territoryGrid.set(key, tile);
    }

    const oldOwner = tile.owner;
    tile.owner = owner;

    if (oldOwner !== owner) {
      const { x, z } = this.parseTileKey(key);
      this.onTerritoryChange?.(x, z, owner);

      EventBus.emit('territory:captured', {
        x: x * this.tileSize,
        z: z * this.tileSize,
        newOwner: owner,
        oldOwner,
      });
    }
  }

  countTerritoryByFaction(): Map<FactionId, number> {
    const counts = new Map<FactionId, number>();

    for (const tile of this.territoryGrid.values()) {
      if (tile.owner) {
        counts.set(tile.owner, (counts.get(tile.owner) ?? 0) + 1);
      }
    }

    return counts;
  }

  /**
   * Get all tile coordinates owned by a specific faction
   */
  getTilesByFaction(factionId: FactionId): Array<{ x: number; z: number }> {
    const tiles: Array<{ x: number; z: number }> = [];

    for (const [key, tile] of this.territoryGrid) {
      if (tile.owner === factionId) {
        const { x, z } = this.parseTileKey(key);
        tiles.push({ x, z });
      }
    }

    return tiles;
  }

  /**
   * Get all tiles with their owners (for rendering)
   */
  getAllTiles(): Array<{ x: number; z: number; owner: FactionId | null }> {
    const tiles: Array<{ x: number; z: number; owner: FactionId | null }> = [];

    for (const [key, tile] of this.territoryGrid) {
      const { x, z } = this.parseTileKey(key);
      tiles.push({ x, z, owner: tile.owner });
    }

    return tiles;
  }

  update(world: World, deltaTime: number): void {
    this.timeSinceUpdate += deltaTime;

    // Check if we should update (either interval elapsed or force update requested)
    if (this.timeSinceUpdate < this.updateInterval && !this.pendingForceUpdate) {
      return;
    }

    this.timeSinceUpdate = 0;
    this.pendingForceUpdate = false;

    // Clear presence counts
    for (const tile of this.territoryGrid.values()) {
      tile.presence.clear();
    }

    // Count building presence in each tile (buildings have larger influence radius)
    const buildings = world.query([ComponentTypes.Position, ComponentTypes.Faction, ComponentTypes.Building]);

    for (const entity of buildings) {
      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;

      // Skip defeated factions - their buildings no longer generate influence
      if (this.defeatedFactions.has(faction.factionId)) continue;

      // Buildings have larger influence radius, capitals have the largest
      const baseRadius = building.buildingType === 'capital' ? 12 : building.buildingType === 'tower' ? 6 : 4;
      const influenceRadius = baseRadius;
      const centerTx = Math.floor(position.x / this.tileSize);
      const centerTz = Math.floor(position.z / this.tileSize);

      for (let dz = -influenceRadius; dz <= influenceRadius; dz++) {
        for (let dx = -influenceRadius; dx <= influenceRadius; dx++) {
          const key = `${centerTx + dx},${centerTz + dz}`;
          let tile = this.territoryGrid.get(key);

          if (!tile) {
            tile = { owner: null, presence: new Map() };
            this.territoryGrid.set(key, tile);
          }

          // Distance-based influence falloff
          const dist = Math.sqrt(dx * dx + dz * dz);
          const influence = Math.max(0, 1 - dist / (influenceRadius + 1));

          // Buildings provide strong base influence
          const buildingInfluence = influence * (building.buildingType === 'capital' ? 5.0 : 2.0);
          tile.presence.set(faction.factionId, (tile.presence.get(faction.factionId) ?? 0) + buildingInfluence);
        }
      }
    }

    // Count unit presence in each tile
    const units = world.query([ComponentTypes.Position, ComponentTypes.Faction, ComponentTypes.Unit]);

    for (const entity of units) {
      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

      // Skip defeated factions - their units no longer generate influence
      if (this.defeatedFactions.has(faction.factionId)) continue;

      // Units influence nearby tiles
      const influenceRadius = 2;
      const centerTx = Math.floor(position.x / this.tileSize);
      const centerTz = Math.floor(position.z / this.tileSize);

      for (let dz = -influenceRadius; dz <= influenceRadius; dz++) {
        for (let dx = -influenceRadius; dx <= influenceRadius; dx++) {
          const key = `${centerTx + dx},${centerTz + dz}`;
          let tile = this.territoryGrid.get(key);

          if (!tile) {
            tile = { owner: null, presence: new Map() };
            this.territoryGrid.set(key, tile);
          }

          // Distance-based influence falloff
          const dist = Math.sqrt(dx * dx + dz * dz);
          const influence = Math.max(0, 1 - dist / (influenceRadius + 1));

          tile.presence.set(faction.factionId, (tile.presence.get(faction.factionId) ?? 0) + influence);
        }
      }
    }

    // Update tile ownership based on presence
    for (const [key, tile] of this.territoryGrid) {
      let maxPresence = 0;
      let dominantFaction: FactionId | null = null;

      for (const [factionId, presence] of tile.presence) {
        if (presence > maxPresence) {
          maxPresence = presence;
          dominantFaction = factionId;
        }
      }

      // Only change ownership if there's significant presence
      if (dominantFaction && maxPresence >= 0.5) {
        if (tile.owner !== dominantFaction) {
          const oldOwner = tile.owner;
          tile.owner = dominantFaction;

          const { x, z } = this.parseTileKey(key);
          this.onTerritoryChange?.(x, z, dominantFaction);

          EventBus.emit('territory:captured', {
            x: x * this.tileSize,
            z: z * this.tileSize,
            newOwner: dominantFaction,
            oldOwner,
          });
        }
      }
    }
  }

  /**
   * Force an immediate territory update on next frame
   */
  forceUpdate(): void {
    this.pendingForceUpdate = true;
  }

  /**
   * Mark a faction as defeated - its buildings/units will no longer generate territory influence
   */
  setFactionDefeated(factionId: FactionId): void {
    this.defeatedFactions.add(factionId);
  }

  /**
   * Check if a faction is defeated
   */
  isFactionDefeated(factionId: FactionId): boolean {
    return this.defeatedFactions.has(factionId);
  }

  clear(): void {
    this.territoryGrid.clear();
    this.defeatedFactions.clear();
  }
}
